import {Applications} from "../views/utility/Types";
import {Data, ErrorReason} from "./APITypes";
import {keycloak} from "../views/utility/KeycloakProvider";

/**
 * Enum for REST-request methods
 * @type
 */
type Method = "POST" | "GET" | "PATCH" | "PUT" | "DELETE";

/**
 * API-requests return a promise resolving to this type.
 * @type
 * @property success
 * @property httpCode – Missing if fetching hasn't finished
 * @property unresolved – Whether the request hasn't been made or resolved yet
 * @property errorReason - Missing if successful
 * @property payload - Missing if unsuccessful
 */
type Resolution<T> = {
  success: boolean;
  httpCode?: number;
  unresolved?: boolean;
  errorReason?: ErrorReason[] | string;
  payload?: T;
};

const externalBackends =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test"
    ? {
        portal: "http://localhost:8000/portal/api/",
      }
    : {
        portal: "/portal/api/",
      };

/**
 * @namespace API
 * @desc Provides an interface to operations in the backend.
 */
const API = {
  url:
    process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test"
      ? "http://localhost:8003/api/"
      : "/api/",
  /**
   * Fetches a resource using the appropriate headers.
   * @function
   * @param path - relative path of the resource
   * @param method - the method by which the fetch is performed
   * @param withCredentials - whether to include the keycloak authorization header
   * @param body - an optional body to attach to the fetch
   * @param abortController - controller to handle aborting of requests
   * @param backend – specification of another backend
   */
  fetchWithHeaders: (
    path: string,
    method?: Method,
    withCredentials?: boolean,
    body?: object,
    abortController?: AbortController,
    backend?: keyof typeof externalBackends,
  ) => {
    const header: HeadersInit = new Headers();
    header.set("Content-Type", "application/json");
    if (withCredentials)
      header.set("Authorization", "Bearer " + keycloak.token);
    const request: RequestInit = {
      method: method,
      headers: header,
      credentials: "same-origin",
      signal: abortController?.signal,
    };
    if (body) request.body = JSON.stringify(body);
    return fetch(
      (backend ? externalBackends[backend] : API.url) + path,
      request,
    );
  },
  /**
   * Evaluates whether a fetch succeeds.
   * @function
   * @param request - the fetched promise to evaluate
   * @returns a non-rejecting promise
   */
  resolveFetch: async <T>(
    request: Promise<Response>,
  ): Promise<Resolution<T>> => {
    try {
      const response = await request;
      if (response.ok) {
        const len = response.headers.get("content-length");
        if (!len || +len == 0)
          return {success: true, httpCode: response.status};
        try {
          const json = await response.json();
          return {
            success: true,
            httpCode: response.status,
            payload: json,
          };
        } catch (errorReason: unknown) {
          return {
            success: false,
            httpCode: response.status,
            errorReason:
              errorReason instanceof Error
                ? errorReason.message
                : String(errorReason),
          };
        }
      }
      const json = (await response.json()) as unknown;
      if (typeof json === "object" && json && "detail" in json) {
        if (
          typeof json.detail === "object" &&
          json.detail &&
          Array.isArray(json.detail) &&
          (json.detail as unknown[]).every(
            (d) => typeof d === "object" && d && "msg" in d,
          )
        )
          return {
            success: false,
            httpCode: response.status,
            errorReason: json.detail as ErrorReason[],
          };
        else
          return {
            success: false,
            httpCode: response.status,
            errorReason: json.detail as string,
          };
      } else
        return {
          success: false,
          httpCode: response.status,
          errorReason: response.statusText,
        };
    } catch (errorReason: unknown) {
      return {
        success: false,
        httpCode: 400,
        errorReason:
          errorReason instanceof Error
            ? errorReason.message
            : String(errorReason),
      };
    }
  },
  /**
   * Attempts to get the list of most recent data objects.
   * @function
   */
  getData: async (): Promise<Resolution<Data>> => {
    try {
      const headers = new Headers({
        "Content-Type": "application/octet-stream",
        Authorization: `Bearer ${keycloak.token}`,
      });

      const request: RequestInit = {
        method: "GET",
        headers,
        credentials: "same-origin",
      };

      const response = await fetch(`${API.url}data`, request);
      if (!response.ok) {
        return {
          success: false,
          httpCode: response.status,
          errorReason: response.statusText,
        };
      }

      const reader = response.body?.getReader();
      if (!reader) {
        return {
          success: false,
          httpCode: 400,
          errorReason: "No response body",
        };
      }

      const chunks: Uint8Array[] = [];
      let done = false;

      while (!done) {
        const {done: readerDone, value} = await reader.read();
        if (value) chunks.push(value);
        done = readerDone;
      }

      const combined = new Uint8Array(
        chunks.reduce((acc, curr) => acc + curr.length, 0),
      );
      let offset = 0;
      chunks.forEach((chunk) => {
        combined.set(chunk, offset);
        offset += chunk.length;
      });

      const text = new TextDecoder().decode(combined);
      return {
        success: true,
        httpCode: response.status,
        payload: text,
      };
    } catch (error) {
      return {
        success: false,
        httpCode: 500,
        errorReason: error instanceof Error ? error.message : String(error),
      };
    }
  },
  /**
   * Attempts to get all applications from the portal backend.
   * @function
   */
  getApplications: async (): Promise<Resolution<Applications>> => {
    return API.resolveFetch<Applications>(
      API.fetchWithHeaders("apps", "GET", true, undefined, undefined, "portal"),
    );
  },
};

export default API;
export {type Resolution};
